import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { apiClient } from '@/services/api'
import { handleError, handleSuccess } from '@/lib/errorHandler'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { TrendingUp, Shield } from 'lucide-react'
import { GoogleLogin } from '@react-oauth/google'

const ALLOWED_EMAIL = 'chisalecharles23@gmail.com'

export function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, _hasHydrated } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(false)
  const [hasRedirected, setHasRedirected] = useState(false)

  // Single redirect effect - only runs once when authenticated
  useEffect(() => {
    if (_hasHydrated && isAuthenticated && !hasRedirected && !authLoading) {
      console.log('[LoginPage] Authenticated, redirecting to dashboard')
      setHasRedirected(true)
      navigate('/dashboard', { replace: true })
    }
  }, [_hasHydrated, isAuthenticated, hasRedirected, authLoading, navigate])

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setIsLoading(true)
    try {
      const response = await apiClient.post('/auth/google', {
        credential: credentialResponse.credential
      })
      
      if (response.data.success && response.data.data) {
        const { user, sessionId } = response.data.data
        
        // Store token in API client
        apiClient.setSession(sessionId)
        
        // Update auth store
        useAuthStore.setState({ 
          user: {
            ...user,
            environment: user.environment || 'demo',
          },
          settings: {
            userId: user.id,
            defaultLeverage: 1,
            riskTolerance: 'medium',
            environment: user.environment || 'demo',
            theme: 'light',
            notifications: true,
          },
          isAuthenticated: true,
        })
        
        handleSuccess('Successfully authenticated')
        
        navigate('/dashboard')
      } else {
        handleError(new Error('Failed to authenticate with Google'))
      }
    } catch (error: any) {
      handleError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleError = () => {
    handleError(new Error('Google Login Failed'))
  }

  // Wait for hydration
  if (!_hasHydrated || (isAuthenticated && _hasHydrated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-sm text-muted-foreground">
            {isAuthenticated ? 'Redirecting to dashboard...' : 'Loading...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-[hsl(142,76%,45%)] to-[hsl(142,76%,40%)] flex items-center justify-center">
            <TrendingUp className="h-8 w-8 text-black" />
          </div>
          <CardTitle className="text-2xl font-bold">AI Trading Platform</CardTitle>
          <CardDescription>
            Secure access restricted to {ALLOWED_EMAIL}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-[hsl(142,76%,45%)]/5 border border-[hsl(142,76%,45%)]/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[hsl(142,76%,45%)]/20 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-[hsl(142,76%,45%)]" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Google Authentication</p>
                <p className="text-xs text-muted-foreground">
                  Login with your authorized Google account
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center flex-col items-center gap-4">
            {isLoading ? (
              <LoadingSpinner size="md" />
            ) : (
              // NOTE: no `useOneTap` — One Tap uses FedCM and its pending prompt gets
              // aborted ("FedCM get() rejects with AbortError") whenever this component
              // unmounts, e.g. while we swap to the spinner / navigate after login.
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                shape="pill"
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
